import React, { useState, useRef, useEffect } from "react";
import { Settings, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import dayjs from "dayjs";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DateRangeField, { DEFAULT_SHORTCUTS } from "../../components/DateRangeField";

const SHORTCUTS = [
  DEFAULT_SHORTCUTS.find((s) => s.label === "Today"), //
  DEFAULT_SHORTCUTS.find((s) => s.label === "This week"), //
  DEFAULT_SHORTCUTS.find((s) => s.label === "Last week"),
  DEFAULT_SHORTCUTS.find((s) => s.label === "This month"),
  DEFAULT_SHORTCUTS.find((s) => s.label === "Last month"),
  // DEFAULT_SHORTCUTS.find((s) => s.label === "This year"),
];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function buildMonthGrid(monthDate) {
  const start = monthDate.startOf("month").startOf("week");
  const end = monthDate.endOf("month").endOf("week");
  const days = [];
  let cur = start;
  while (cur.isBefore(end) || cur.isSame(end, "day")) {
    days.push(cur);
    cur = cur.add(1, "day");
  }
  return days;
}

function MonthGrid({ monthDate, onNavigate, showPrevArrow, showNextArrow, range, hoverDate, onDayClick, onDayHover }) {
  const days = buildMonthGrid(monthDate);
  const [rangeStart, rangeEnd] = range;

  const isInRange = (d) => {
    if (!rangeStart) return false;
    const end = rangeEnd || hoverDate;
    if (!end) return false;
    const lo = rangeStart.isBefore(end) ? rangeStart : end;
    const hi = rangeStart.isBefore(end) ? end : rangeStart;
    return d.isAfter(lo, "day") && d.isBefore(hi, "day");
  };

  const isEndpoint = (d) => (rangeStart && d.isSame(rangeStart, "day")) || (rangeEnd && d.isSame(rangeEnd, "day"));

  return (
    <div className="w-[280px]">
      <div className="flex items-center justify-between px-2 mb-2">
        <button onClick={() => showPrevArrow && onNavigate(-1)} className={`p-1 rounded hover:bg-gray-100 ${!showPrevArrow ? "invisible" : ""}`}>
          <ChevronLeft size={16} className="text-gray-400" />
        </button>
        <span className="text-sm font-medium text-gray-800">{monthDate.format("MMM YYYY")}</span>
        <button onClick={() => showNextArrow && onNavigate(1)} className={`p-1 rounded hover:bg-gray-100 ${!showNextArrow ? "invisible" : ""}`}>
          <ChevronRight size={16} className="text-gray-400" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-y-1 px-2">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[11px] text-gray-400 font-medium py-1">
            {w}
          </div>
        ))}

        {days.map((d) => {
          const outside = !d.isSame(monthDate, "month");
          const inRange = isInRange(d);
          const endpoint = isEndpoint(d);

          return (
            <div key={d.format("YYYY-MM-DD")} className="flex items-center justify-center relative">
              {inRange && <div className="absolute inset-y-0 left-0 right-0 bg-sky-50" />}
              <button
                onClick={() => onDayClick(d)}
                onMouseEnter={() => onDayHover(d)}
                className={`relative z-10 w-7 h-7 text-xs rounded-full transition-colors
                  ${outside ? "text-gray-300" : "text-gray-700"}
                  ${endpoint ? "bg-blue-600 text-white font-medium" : ""}
                  ${!endpoint && !outside ? "hover:bg-sky-100" : ""}
                `}
              >
                {d.date()}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function staffLabel(user) {
  return [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || `User ${user.id}`;
}

function StaffMultiSelect({ staffUsers, selectedStaffIds, onStaffChange }) {
  const selectedUsers = staffUsers.filter((u) => selectedStaffIds.includes(u.id));

  return (
    <Autocomplete
      multiple
      size="small"
      options={staffUsers}
      value={selectedUsers}
      onChange={(event, newValue) => onStaffChange(newValue.map((u) => u.id))}
      getOptionLabel={staffLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disableCloseOnSelect
      limitTags={2}
      sx={{
        minWidth: 200,
        maxWidth: 280,
        "& .MuiOutlinedInput-root": {
          padding: "6px 8px !important",
          minHeight: "auto",
          borderRadius: "6px",
          backgroundColor: "#fff",
          transition: "background-color 150ms",
        },
        "& .MuiOutlinedInput-root:hover": {
          backgroundColor: "#f9fafb",
        },
        "& .MuiOutlinedInput-root.Mui-focused": {
          backgroundColor: "#fff",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#e5e7eb",
          borderWidth: "1px",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#e5e7eb",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#7dd3fc",
          borderWidth: "1px",
        },
        "& .MuiAutocomplete-input": {
          fontSize: "12px",
          padding: "0 4px !important",
        },
        "& .MuiAutocomplete-tag": {
          margin: "2px",
        },
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id} style={{ fontSize: "12px", minHeight: "auto", padding: "4px 10px" }}>
          <Checkbox icon={uncheckedIcon} checkedIcon={checkedIcon} style={{ marginRight: 6, padding: 4 }} checked={selected} size="small" />
          {staffLabel(option)}
        </li>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={staffLabel(option)}
            size="small"
            sx={{
              height: 16,
              fontSize: "12px",
              borderRadius: "4px",
              backgroundColor: "#f3f4f6",
              color: "#4b5563",
              "& .MuiChip-label": { padding: "0 5px" },
              "& .MuiChip-deleteIcon": { fontSize: "13px", margin: "0 2px 0 -2px", color: "#9ca3af" },
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={selectedUsers.length ? "" : "Staff"}
          sx={{
            "& .MuiInputBase-root": { fontSize: "12px", color: "#374151" },
            "& .MuiInputBase-input::placeholder": { color: "#6b7280", opacity: 1 },
          }}
        />
      )}
    />
  );
}

export default function FilterBar({ period, onPeriodChange, from, to, onCustomRangeChange, onManageStages, staffUsers = [], selectedStaffIds = [], onStaffChange }) {
  return (
    <div className="flex items-center gap-2 flex-wrap bg-white border border-gray-100 rounded-xl shadow-sm px-3 py-2 mb-4">
      <DateRangeField
        from={from}
        to={to}
        onCustomRangeChange={onCustomRangeChange}
        disableFuture={true}
        shortcuts={SHORTCUTS}
        defaultRange={() => {
          const t = dayjs();
          return [t.subtract(6, "day"), t]; // defaults to "Last 7 days" until the user picks something else
        }}
      />

      <span className="w-px h-5 bg-gray-200 mx-1" />

      <StaffMultiSelect staffUsers={staffUsers} selectedStaffIds={selectedStaffIds} onStaffChange={onStaffChange} />

      <button onClick={onManageStages} className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-gray-200 text-gray-500 hover:bg-gray-50">
        <Settings size={13} />
        <span>Manage stages</span>
      </button>
    </div>
  );
}
